import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import AddToCalendar from '@/components/AddToCalendar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderData();
  }, [orderId]);

  const fetchOrderData = async () => {
    try {
      const [orderRes, ticketsRes] = await Promise.all([
        axios.get(`${API_URL}/orders/${orderId}`),
        axios.get(`${API_URL}/orders/${orderId}/tickets`)
      ]);
      
      setOrder(orderRes.data);
      setTickets(ticketsRes.data);
      
      if (orderRes.data.event_id) {
        const eventRes = await axios.get(`${API_URL}/events/${orderRes.data.event_id}`);
        setEvent(eventRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento ordine');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async (ticketId) => {
    try {
      const response = await axios.get(`${API_URL}/tickets/${ticketId}/qr`);
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${response.data.qr_code_base64}`;
      link.download = `ticket-${ticketId}.png`;
      link.click();
      toast.success('QR code scaricato!');
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-lg text-gray-600">Caricamento...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ordine non trovato</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 py-12 px-4" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto">
        <Card className="mb-8">
          <CardContent className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" data-testid="success-icon" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Ordine Completato!</h1>
            <p className="text-gray-600 text-lg mb-6">
              I tuoi biglietti sono stati generati con successo.
            </p>
            {event && (
              <div className="text-gray-700 mb-6">
                <p className="font-semibold text-xl">{event.title}</p>
                <p className="text-gray-600">{event.location}</p>
              </div>
            )}
            
            {/* Add to Calendar Button */}
            {tickets.length > 0 && (
              <div className="mb-6">
                <AddToCalendar ticketId={tickets[0].id} variant="default" size="lg" />
                <p className="text-sm text-gray-500 mt-2">
                  Aggiungi l'evento al tuo calendario con promemoria automatici
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-center gap-2 text-blue-600 bg-blue-50 p-4 rounded-lg">
              <Mail className="w-5 h-5" />
              <span>Una email di conferma è stata inviata a: <strong>{order.buyer_email}</strong></span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">I Tuoi Biglietti</h2>
            <div className="space-y-4">
              {tickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className="border-2 border-gray-200 rounded-xl p-6"
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Biglietto #{index + 1}
                      </h3>
                      <p className="text-gray-600 mb-1">
                        <span className="font-medium">Tipo:</span> {ticket.ticket_type?.name}
                      </p>
                      <p className="text-gray-600 mb-1">
                        <span className="font-medium">Intestatario:</span> {ticket.holder_name}
                      </p>
                      {ticket.zones && ticket.zones.length > 0 && (
                        <p className="text-gray-600">
                          <span className="font-medium">Accesso a:</span> {ticket.zones.map(z => z.name).join(', ')}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => downloadQR(ticket.id)}
                      variant="outline"
                      size="sm"
                      data-testid={`download-qr-${ticket.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica QR
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Riepilogo Ordine</h3>
              <div className="flex justify-between text-gray-600 mb-1">
                <span>Ordine ID:</span>
                <span className="font-mono text-sm">{order.id}</span>
              </div>
              <div className="flex justify-between text-gray-600 mb-1">
                <span>Biglietti:</span>
                <span>{tickets.length}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 mt-4 pt-4 border-t border-gray-300">
                <span>Totale:</span>
                <span data-testid="order-total">€{order.total_amount.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
